import { colors } from '@atlaskit/theme';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { useContextualMenu } from '@databases/contextual-menu';
import ValidationStatus, {
  ValidationStatusContainer,
} from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import {
  deselectAll,
  selectFieldSingle,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import { Adjacents } from '@databases/redux/cellSelections/selectors';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import { SkeletonItem } from '@databases/skeleton';
import {
  getDatabaseFieldRequired,
  getExistingFieldNames,
  isUniqueFieldName,
} from '@databases/utils/database/utils';
import { noop } from 'lodash';
import React, { forwardRef, RefObject, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { InlineEditContainer, InlineEditStateless } from '../../InlineEdit';
import InlineEditWrapper from '../../InlineEditWrapper';
import FieldTypeIcon from '../FieldTypeIcon';

import FieldNameContainer from './FieldNameContainer';
import FieldNameEdit, { FieldNameEditContainer } from './FieldNameEdit';
import FieldNameView, {
  FieldNameViewContainer,
  FieldNameViewProps,
} from './FieldNameView';

const StyledInlineEditWrapper = styled(InlineEditWrapper)<{ canEdit?: boolean }>`
  > ${ValidationStatusContainer} > ${InlineEditContainer} {
    flex-grow: 1;
    display: flex;
    justify-content: center;

    > div {
      flex-grow: 0;
    }
  }

  > ${ValidationStatusContainer}
    > ${InlineEditContainer}
    > div
    > ${ValidationStatusContainer}
    > ${FieldNameEditContainer}
    > div {
    border: none;
    background: none;

    input {
      font-size: 12px;
      height: 21px;
      line-height: 18px;
      padding: 1px 4px 2px 4px;
      color: ${colors.N300};
    }
  }

  > ${FieldNameViewContainer},
    > ${ValidationStatusContainer}
    > ${InlineEditContainer}
    > div
    > ${FieldNameViewContainer} {
    letter-spacing: 0;
    padding: 1px 4px 2px 4px;
  }
`;

export interface FieldNameInlineEditStatelessProps extends FieldNameViewProps {
  isReadonly?: boolean;
  showIcon?: boolean;
  isSelected?: boolean;
  hoverDeleteButton?: boolean;
  selectedAdjacentCells?: Adjacents;
}

/**
 * Renders an inline edit field to edit the field name.
 * When hitting the enter button or on blur the input field, the database draft with the changed field name is automatically saved.
 */
// TODO: This component is not actually used for inline editing since we can no longer edit the field name inline.
// This component should just render field name and icon, but needs to keep logic for selections, specifically style logic.
export const FieldNameInlineEditStateless = forwardRef<
  HTMLInputElement,
  FieldNameInlineEditStatelessProps
>(function FieldNameInlineEditStateless(
  {
    contentId,
    fieldId,
    showIcon,
    isReadonly,
    isSelected,
    hoverDeleteButton,
    selectedAdjacentCells,
  },
  inputRef
) {
  const fieldNodeRef = useRef<RefObject<HTMLDivElement> | undefined>(undefined);

  const { formatMessage } = useIntl();

  const scope = useScope();
  const { showPanel } = usePanel();
  const { closeMenu } = useContextualMenu();
  const client = useAnalytics();

  const [field, validate] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const field = getDatabaseFieldRequired(databaseDraft, fieldId);
    const validate = isUniqueFieldName(
      field.name,
      getExistingFieldNames(databaseDraft, field)
    );
    return [field, validate] as const;
  });

  const dispatch = defaultStoreUtils.useDispatch();

  const handleIsEditingChange = useCallback(() => {
    dispatch(deselectAll());
    dispatch(
      selectFieldSingle({
        contentId,
        fieldId,
      })
    );
    dispatch(setSoftSelection({ value: true }));
    showPanel('FieldSettingsPanel', { fieldId });
    closeMenu();

    client.sendDatabaseFieldUpdatedEvent({
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
    });
  }, [dispatch, contentId, fieldId, showPanel, closeMenu, client]);

  const canEdit = !isReadonly;

  return (
    <FieldNameContainer ref={fieldNodeRef.current}>
      {showIcon && (
        <SkeletonItem isButton>
          <FieldTypeIcon type={field.type} noTooltip />
        </SkeletonItem>
      )}
      <StyledInlineEditWrapper
        hideBorderOnFocus
        isDraggable={false}
        canEdit={canEdit}
        isSelected={isSelected}
        markAsDeleted={hoverDeleteButton}
        selectedAdjacentCells={selectedAdjacentCells}
      >
        <>
          {!canEdit ? (
            <SkeletonItem minWidth={80}>
              <FieldNameView contentId={contentId} fieldId={fieldId} />
            </SkeletonItem>
          ) : (
            <ValidationStatus
              error={
                validate
                  ? undefined
                  : formatMessage(messages['validation.unique-field-name'])
              }
            >
              {() => (
                <InlineEditStateless<string>
                  readView={() => (
                    <SkeletonItem minWidth={80}>
                      <FieldNameView contentId={contentId} fieldId={fieldId} />
                    </SkeletonItem>
                  )}
                  editView={() => (
                    <SkeletonItem minWidth={80}>
                      <FieldNameEdit
                        contentId={contentId}
                        fieldId={fieldId}
                        noValidation
                        showPlaceholder
                        ref={inputRef}
                      />
                    </SkeletonItem>
                  )}
                  isEditing={false}
                  isReadOnly={!canEdit}
                  onIsEditingChange={handleIsEditingChange}
                  value={field.name}
                  onReset={noop}
                  preserveEditWidth
                />
              )}
            </ValidationStatus>
          )}
        </>
      </StyledInlineEditWrapper>
    </FieldNameContainer>
  );
});

interface FieldNameInlineEditProps
  extends Omit<FieldNameInlineEditStatelessProps, 'isEditing' | 'onIsEditingChange'> {
  /** If true, the field will be in edit mode the first time it is rendered. */
  initialIsEditing?: boolean;
}

const FieldNameInlineEdit = forwardRef<HTMLInputElement, FieldNameInlineEditProps>(
  function FieldNameInlineEdit({ ...props }, inputRef) {
    return <FieldNameInlineEditStateless {...props} ref={inputRef} />;
  }
);

export default FieldNameInlineEdit;
